import {createSelector} from 'reselect'
import {selectTaskManagement} from '../selectors'
import {arrayToSet} from "../../atUtils"
import {ALL_FILTER} from "../../workflowStatus/components/statusFilter/utils"
import {Feature, isEnabled} from "../../features"
import {getCustomers, getProjects, getTasks, getWorkflowStatuses} from '../../entities/selectors'
import {
    createIntersectedCPSelection,
    getCustomerIdsFilteredByAssignments,
    getProjectIdsFilteredByAssignments,
    getProjectsFilteredByAssignments,
    getTaskIdsFilteredByAssignments,
    isCustomerAssignedForManage,
    isPossibleToManageProject
} from './utils'
import {TaskManagementViewMode} from '../constants'
import _ from 'underscore'
import {isTWSEnabled} from '../../features/selectors'
import {canEditHierarchy} from '../../loggedInUser/selectors'

export const getCommonState = createSelector(
    selectTaskManagement,
    (tmState) => tmState.common
)

export const getRecentlyCreatedCustomerIds = state => getCommonState(state).recentlyCreatedCustomerIds
export const getRecentlyCreatedProjectIds = state => getCommonState(state).recentlyCreatedProjectIds
export const getLastCreatedTaskIds = state => getCommonState(state).lastCreatedTaskIds

export const isShowOnlyManageableTasks = (state) => {
    return getCommonState(state).showOnlyManageableTasks
}

export const getSelectedViewMode = createSelector(
    getCommonState,
    (commonState) => commonState.viewMode
)

export const isShowViewMode = createSelector(
    isTWSEnabled,
    (enabled) => enabled
)

export const getViewMode = createSelector(
    isShowViewMode,
    getSelectedViewMode,
    (showViewMode, selectedViewMode) => showViewMode ? selectedViewMode : TaskManagementViewMode.TABLE
)

export const getSingleCPSelection = createSelector(
    getCommonState,
    getCustomers,
    getProjects,
    (commonState, customers, projects) => {
        let {
            projectId,
            customerId
        } = commonState.singleCustomerProjectSelection

        if (projectId !== null && !projects[projectId]) {
            projectId = null
        }
        if (customerId !== null && !customers[customerId]) {
            customerId = null
            projectId = null
        }

        return {
            customerId,
            projectId,
        }
    }
)

export const getFilterCPSelection = createSelector(
    getCommonState,
    (commonState) => commonState.filterCustomerProjectSelection
)

export const getCustomerProjectNameFilter = state => getCommonState(state).customerProjectNameFilter

export const getIntersectedCPSelection = createSelector(
    getFilterCPSelection,
    getSingleCPSelection,
    getCustomers,
    getProjects,
    (filterCPSelection, singleCPSelection, customers, projects) => {
        return createIntersectedCPSelection(
            filterCPSelection,
            singleCPSelection,
            customers,
            projects,
        ).toPlainObject()
    }
)

export const isSingleProjectSelected = createSelector(
    getSingleCPSelection,
    (singleCPSelection) => singleCPSelection.customerId !== null &&
                           singleCPSelection.projectId !== null
)

export const isSingleCustomerSelected = createSelector(
    getSingleCPSelection,
    (singleCPSelection) => singleCPSelection.customerId !== null &&
                           singleCPSelection.projectId === null
)

const getTaskStatusFilterInitial = createSelector(
    getCommonState,
    (commonState) => commonState.taskStatusFilter
)

const removeDeletedStatusesFromFilter = (taskStatusFilter, statusesMap) => {
    let filter = {
        allSelected: false,
        statusIds: [],
        statusTypes: []
    }

    filter.statusIds = taskStatusFilter.statusIds.filter((statusId) => !!statusesMap[statusId])
    if (filter.statusIds.length === 0 && taskStatusFilter.statusTypes.length === 0)
        filter.allSelected = true
    filter.statusTypes = taskStatusFilter.statusTypes

    return filter
}

export const getTaskStatusFilter = createSelector(
    getTaskStatusFilterInitial,
    getWorkflowStatuses,
    (filter, statusesMap) => {
        if (!isEnabled(Feature.TWS) && filter.statusIds.length > 0)
            return ALL_FILTER
        else {
            return removeDeletedStatusesFromFilter(filter, statusesMap)
        }
    }
)

export const getTaskNameFilter = createSelector(
    getCommonState,
    (commonState) => commonState.taskNameFilter
)

export const getRecentlyCreatedTaskIds = state => getCommonState(state).recentlyCreatedTaskIds

export const getManagementAssignments = createSelector(
    getCommonState,
    (commonState) => commonState.managementAssignments
)

export const getCustomersManagementAssignments = createSelector(
    getManagementAssignments,
    (managementAssignments) => managementAssignments.customers
)

export const getViewerSettings = createSelector(
    getCommonState,
    (commonState) => commonState.viewerSettings
)

export const isViewerHasAccessToAllManagementScope = createSelector(
    getViewerSettings,
    (viewerSettings) => viewerSettings.hasAccessToAllManagementScope
)

export const isViewerHasNoTeamAccess = createSelector(
    getViewerSettings,
    (viewerSettings) => viewerSettings.noTeamAccess
)

export const isViewerHasManageableTasks = createSelector(
    getCommonState,
    (commonState) => commonState.hasManageableTasks
)

export const canManageSelectedProject = createSelector(
    getViewerSettings,
    getManagementAssignments,
    getSingleCPSelection,
    getProjects,
    (viewerSettings, ma, cpSelection) => {
        return !!cpSelection.projectId && viewerSettings.hasEditHierarchyRight &&
               (viewerSettings.hasAccessToAllManagementScope || !!ma.customers[cpSelection.customerId] || !!ma.projects[cpSelection.projectId])
    }
)

export const isSelectedProjectArchived = createSelector(
    getSingleCPSelection,
    getProjects,
    (cpSelection, projects) => {
        return !!cpSelection.projectId && !!projects[cpSelection.projectId] && projects[cpSelection.projectId].archived
    }
)

export const getManageableProjects = createSelector(
    getViewerSettings,
    getProjects,
    getManagementAssignments,
    (viewerSettings, projects, ma) => {
        return viewerSettings.hasAccessToAllManagementScope
               ? projects
               : getProjectsFilteredByAssignments(Object.values(projects), ma)
    }
)

export const isPossibleToAddCustomers = createSelector(
    getViewerSettings,
    (viewerSettings) => {
        return viewerSettings.hasEditHierarchyRight && viewerSettings.hasAccessToAllManagementScope
    }
)

export const isPossibleToAddProjects = createSelector(
    getViewerSettings,
    getCustomersManagementAssignments,
    (viewerSettings, customersMA) => {
        return viewerSettings.hasEditHierarchyRight &&
               (viewerSettings.hasAccessToAllManagementScope || !_.isEmpty(customersMA))
    }
)

export const getManageableTaskIds = createSelector(
    getViewerSettings,
    getTasks,
    getManagementAssignments,
    (viewerSettings, tasks, ma) => {
        return viewerSettings.hasAccessToAllManagementScope
               ? arrayToSet(Object.keys(tasks))
               : arrayToSet(getTaskIdsFilteredByAssignments(Object.values(tasks), ma))
    }
)

export const getEditableTaskIds = createSelector(
    canEditHierarchy,
    getManageableTaskIds,
    (canEditHierarchy, tIds) => {
        return canEditHierarchy ? tIds : {}
    }
)


export const getManageableProjectIds = createSelector(
    getViewerSettings,
    getProjects,
    getManagementAssignments,
    (viewerSettings, projects, ma) => {
        return viewerSettings.hasAccessToAllManagementScope
               ? arrayToSet(Object.keys(projects))
               : arrayToSet(getProjectIdsFilteredByAssignments(Object.values(projects), ma))
    }
)

export const getManageableProjectIdsWithExcludeSelf = createSelector(
    getViewerSettings,
    getProjects,
    getManagementAssignments,
    (viewerSettings, projects, ma) => {
        return viewerSettings.hasAccessToAllManagementScope
            ? arrayToSet(Object.keys(projects))
            : arrayToSet(getProjectIdsFilteredByAssignments(Object.values(projects), ma, false))
    }
)

export const getManageableProjectsWithExcludeSelf = createSelector(
    getViewerSettings,
    getProjects,
    getManagementAssignments,
    (viewerSettings, projects, ma) => {
        return viewerSettings.hasAccessToAllManagementScope
            ? projects
            : getProjectsFilteredByAssignments(Object.values(projects), ma, false)
    }
)

export const isPossibleToAddTasks = createSelector(
    getViewerSettings,
    getManageableProjectsWithExcludeSelf,
    isPossibleToAddProjects,
    (viewerSettings, maProjects, isPossibleToAddProjects) => {
        return viewerSettings.hasEditHierarchyRight
               && (isPossibleToAddProjects || Object.values(maProjects).some(p => !p.archived))
    }
)

export const getEditableProjectIds = createSelector(
    canEditHierarchy,
    getManageableProjectIds,
    (canEditHierarchy, pIds) => {
        return canEditHierarchy ? pIds : {}
    }
)

export const getEditableProjectIdsWithExcludeSelf = createSelector(
    canEditHierarchy,
    getManageableProjectIdsWithExcludeSelf,
    (canEditHierarchy, pIds) => {
        return canEditHierarchy ? pIds : {}
    }
)

export const getManageableCustomerIds = createSelector(
    getViewerSettings,
    getCustomers,
    getCustomersManagementAssignments,
    (viewerSettings, customers, customerMA) => {
        if (!viewerSettings.hasEditHierarchyRight) {
            return {}
        }

        const customerIds = Object.keys(customers)
        return viewerSettings.hasAccessToAllManagementScope
               ? arrayToSet(customerIds)
               : arrayToSet(getCustomerIdsFilteredByAssignments(customerIds, customerMA))
    }
)

export const getManageableCustomerIdsWithExcludeSelf = createSelector(
    getViewerSettings,
    getCustomers,
    getCustomersManagementAssignments,
    (viewerSettings, customers, customerMA) => {
        if (!viewerSettings.hasEditHierarchyRight) {
            return {}
        }

        const customerIds = Object.keys(customers)
        return viewerSettings.hasAccessToAllManagementScope
            ? arrayToSet(customerIds)
            : arrayToSet(getCustomerIdsFilteredByAssignments(customerIds, customerMA, false))
    }
)


export const getEditableCustomerIds = createSelector(
    canEditHierarchy,
    getManageableCustomerIds,
    (canEditHierarchy, cIds) => {
        return canEditHierarchy ? cIds : {}
    }
)

export const getEditableCustomerIdsWithExcludeSelf = createSelector(
    canEditHierarchy,
    getManageableCustomerIdsWithExcludeSelf,
    (canEditHierarchy, cIds) => {
        return canEditHierarchy ? cIds : {}
    }
)

export const getCustomerIdsWhereCanManageLeastOneProject = createSelector(
    canEditHierarchy,
    getCustomers,
    getEditableCustomerIdsWithExcludeSelf,
    getManageableProjectsWithExcludeSelf,
    (canEditHierarchy, allCustomers, manageableCustomerIds, manageableProjects) => {
        const allCustomerIds = Object.keys(allCustomers)
        let filteredCustomerIds = []
        if (canEditHierarchy) {
            filteredCustomerIds = allCustomerIds.filter((cId) =>
                !!manageableCustomerIds[cId] || manageableProjects.some(project => project.customerId === +cId)
            )
        }
        return arrayToSet(filteredCustomerIds)
    }
)

export const isViewerCanManageAllTasksOfCPSelection = createSelector(
    getViewerSettings,
    getSingleCPSelection,
    getProjects,
    getManagementAssignments,
    (viewerSettings, cpSelection, projects, ma) => {
        if (!viewerSettings.hasEditHierarchyRight) {
            return false
        }

        const customerId = cpSelection.customerId
        const projectId = cpSelection.projectId

        return viewerSettings.hasAccessToAllManagementScope
               || (customerId !== null && isCustomerAssignedForManage(ma.customers, customerId, false))
               || (projectId !== null && !!projects[projectId] && isPossibleToManageProject(projects[projectId], viewerSettings, ma.customers, ma.projects))
    }
)

export const isNeedToShowScopeFilter = createSelector(
    isViewerHasManageableTasks,
    isViewerCanManageAllTasksOfCPSelection,
    (isViewerHasManageableTasks, isViewerCanManageAllTasksOfCPSelection) => isViewerHasManageableTasks && !isViewerCanManageAllTasksOfCPSelection
)

export const isNeedToShowSelection = createSelector(
    isNeedToShowScopeFilter,
    isShowOnlyManageableTasks,
    isViewerCanManageAllTasksOfCPSelection,
    (isNeedToShowScopeFilter, isShowOnlyManageableTasks, isViewerCanManageAllTasksOfCPSelection) => {
        return (isNeedToShowScopeFilter && isShowOnlyManageableTasks)
               || (!isNeedToShowScopeFilter && isViewerCanManageAllTasksOfCPSelection)
    }
)

export const isCPPanelClosed = createSelector(
    getCommonState,
    (common) => common.cpPanelClosed
)

export const isBulkOperationsPanelShown = createSelector(
    getCommonState,
    (commonState) => commonState.bulkOperationsPanelShown
)

export const getTasksSelectionFilters = createSelector(
    getIntersectedCPSelection,
    getTaskStatusFilter,
    getTaskNameFilter,
    (cpSelectionFilter, taskStatusFilter, taskNameFilter) => {
        return {
            cpSelection: cpSelectionFilter,
            taskStatus: taskStatusFilter,
            wordFilter: taskNameFilter,
        }
    }
)



// WEBPACK FOOTER //
// ./web/js/taskManagement/common/selectors.js